import { useState, useEffect, useRef } from "react";
import {
  Flex,
  Input,
  Button,
  Box,
  Text,
  Badge,
  HStack,
  Tooltip,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import { InfoIcon, SearchIcon } from "@chakra-ui/icons";
import { format, subDays } from "date-fns";
import AsyncSelect from "react-select/async";
import { useColorModeValue } from "@chakra-ui/react";

const dateRanges = [
  { label: "7 dias", days: 7 },
  { label: "30 dias", days: 30 },
  { label: "90 dias", days: 90 },
  { label: "180 dias", days: 180 },
  { label: "1 ano", days: 365 },
];
interface SearchFunction {
  (searchTerm: string, page: number): void;
}
interface ApplyFiltersFunction {
  (
    startDate: string,
    endDate: string,
    corbanId: number,
    promoterId: number,
    commercialManagerId: string,
    sellerId: string
  ): void;
}

interface UpdateOptionsFunction {
  (options: Array<any>): void;
}

const FilterComponent = ({
  applyCorbanSearch = () => {},
  applyPromoterSearch = () => {},
  applyResponsavelSearch = () => {},
  applySellerSearch = () => {},
  applyFilters = () => {},
  corbanOptions = [],
  promoterOptions = [],
  responsavelOptions = [],
  sellerOptions = [],
  isLoadingPromoter = false,
  isLoading = false,
  isLoadingResponsavel = false,
  isLoadingSeller = false,
  updateSellerOptions,
  updateCorbanOptions,
  updatePromoterOptions,
  updateResponsavelOptions,
}: {
  applyCorbanSearch: SearchFunction;
  applyPromoterSearch: SearchFunction;
  applyResponsavelSearch: SearchFunction;
  applySellerSearch: SearchFunction;
  applyFilters: ApplyFiltersFunction;
  corbanOptions: Array<any>;
  promoterOptions: Array<any>;
  responsavelOptions: Array<any>;
  sellerOptions: Array<any>;
  isLoadingPromoter: boolean;
  isLoading: boolean;
  isLoadingResponsavel: boolean;
  isLoadingSeller: boolean;
  updateSellerOptions: UpdateOptionsFunction;
  updateCorbanOptions: UpdateOptionsFunction;
  updatePromoterOptions: UpdateOptionsFunction;
  updateResponsavelOptions: UpdateOptionsFunction;
}) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [corbanId, setCorbanId] = useState(0);
  const [promoterId, setPromoterId] = useState(0);
  const [sellerId, setSellerId] = useState("");
  const corbanRef = useRef<HTMLDivElement>(null);
  const promoterRef = useRef<HTMLDivElement>(null);
  const responsavelRef = useRef<HTMLDivElement>(null);
  const sellerRef = useRef<HTMLDivElement>(null);
  const [commercialManagerId, setCommercialManagerId] = useState("");
  const [selectedRange, setSelectedRange] = useState("");
  const [rangeIndicator, setRangeIndicator] = useState({
    color: "gray",
    text: "Selecione um período",
  });
  const [selectedCorban, setSelectedCorban] = useState(null);
  const [corbanSearch, setCorbanSearch] = useState("");
  const [corbanPage, setCorbanPage] = useState(10);

  const [selectedPromoter, setSelectedPromoter] = useState(null);
  const [promoterSearch, setPromoterSearch] = useState("");
  const [corbanMenuIsOpen, setCorbanMenuIsOpen] = useState(false);
  const [promoterMenuIsOpen, setPromoterMenuIsOpen] = useState(false);
  const [promoterPage, setPromoterPage] = useState(10);

  const [selectedResponsavel, setSelectedResponsavel] = useState(null);
  const [responsavelSearch, setResponsavelSearch] = useState("");
  const [responsavelMenuIsOpen, setResponsavelMenuIsOpen] = useState(false);
  const [responsavelPage, setResponsavelPage] = useState(10);

  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerSearch, setSellerSearch] = useState("");
  const [sellerMenuIsOpen, setSellerMenuIsOpen] = useState(false);
  const [sellerPage, setSellerPage] = useState(10);

  const initialMountRef = useRef(true);

  const controlBorderColor = useColorModeValue("gray.200", "gray.600");
  const controlBackgroundColor = useColorModeValue("white", "gray.700");
  const menuBackgroundColor = useColorModeValue("white", "gray.700");
  const optionSelectedBackgroundColor = useColorModeValue(
    "green.500",
    "green.600"
  );
  const optionFocusedBackgroundColor = useColorModeValue(
    "gray.100",
    "gray.600"
  );
  const optionTextColor = useColorModeValue("gray.800", "white");
  const placeholderTextColor = useColorModeValue("gray.500", "gray.400");
  const loadingIndicatorColor = useColorModeValue("green.500", "green.300");
  const textColor = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (corbanRef.current && !corbanRef.current.contains(event.target)) {
        setCorbanMenuIsOpen(false);
      }
      if (promoterRef.current && !promoterRef.current.contains(event.target)) {
        setPromoterMenuIsOpen(false);
      }
      if (
        responsavelRef.current &&
        !responsavelRef.current.contains(event.target)
      ) {
        setResponsavelMenuIsOpen(false);
      }
      if (sellerRef.current && !sellerRef.current.contains(event.target)) {
        setSellerMenuIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!corbanId) setCorbanId(0);
  }, [corbanId]);

  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false;

      const end = new Date();
      const start = subDays(end, 30);

      setStartDate(format(start, "yyyy-MM-dd"));
      setEndDate(format(end, "yyyy-MM-dd"));
      setSelectedRange("30");

      applyFilters(
        format(start, "yyyy-MM-dd"),
        format(end, "yyyy-MM-dd"),
        0,
        0,
        "",
        ""
      );
    }
  }, [applyFilters]);

  const handleDateRangeSelect = (days: any) => {
    const end = new Date();
    const start = subDays(end, days);

    setStartDate(format(start, "yyyy-MM-dd"));
    setEndDate(format(end, "yyyy-MM-dd"));
    setSelectedRange(days.toString());
  };

  useEffect(() => {
    if (!startDate || !endDate) {
      setRangeIndicator({ color: "gray", text: "Selecione um período" });
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays <= 45) {
      setRangeIndicator({ color: "blue", text: "Visualização Diária" });
    } else if (diffDays <= 180) {
      setRangeIndicator({ color: "purple", text: "Visualização Semanal" });
    } else {
      setRangeIndicator({ color: "orange", text: "Visualização Mensal" });
    }
  }, [startDate, endDate]);

  const formattedCorbanOptions = corbanOptions.map((corban) => ({
    value: corban.id,
    label: corban.name,
  }));

  const formattedPromoterOptions = promoterOptions.map((promoter) => ({
    value: promoter.id,
    label: promoter.name,
  }));

  const formattedSellerOptions = sellerOptions.map((seller) => ({
    value: seller.id,
    label: `${seller.name}  ${seller.lastName}`,
  }));

  // Add formatted options for responsavel
  const formattedResponsavelOptions =
    responsavelOptions && responsavelOptions.length > 0
      ? responsavelOptions.map((responsavel) => ({
          value: responsavel.id,
          label: responsavel.name,
        }))
      : [];

  type NumberOptionType = { value: number; label: string };
  type StringOptionType = { value: string; label: string };

  const loadCorbanOptions = (
    inputValue: string,
    callback: (options: NumberOptionType[]) => void
  ) => {
    const filteredOptions = formattedCorbanOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    const allOption = { value: 0, label: "Todos" };
    callback([allOption, ...filteredOptions]);
  };

  const loadPromotersOptions = (
    inputValue: string,
    callback: (options: NumberOptionType[]) => void
  ) => {
    const filteredOptions = formattedPromoterOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    const allOption = { value: 0, label: "Todos" };
    callback([allOption, ...filteredOptions]);
  };

  const loadResponsavelOptions = (
    inputValue: string,
    callback: (options: StringOptionType[]) => void
  ) => {
    if (
      !formattedResponsavelOptions ||
      formattedResponsavelOptions.length === 0
    ) {
      callback([{ value: "", label: "Todos" }]);
      return;
    }

    const filteredOptions = formattedResponsavelOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    const allOption = { value: "", label: "Todos" };
    callback([allOption, ...filteredOptions]);
  };

  const loadSellerOptions = (
    inputValue: string,
    callback: (options: StringOptionType[]) => void
  ) => {
    if (!formattedSellerOptions || formattedSellerOptions.length === 0) {
      // If no options are available, just return the "Todos" option
      callback([{ value: "", label: "Todos" }]);
      return;
    }

    const filteredOptions = formattedSellerOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    const allOption = { value: "", label: "Todos" };
    callback([allOption, ...filteredOptions]);
  };

  const handlePromoterChange = (selectedOption: any) => {
    setSelectedPromoter(selectedOption);
    setPromoterId(selectedOption ? selectedOption.value : 0);
    const updatedPromoterOptions = promoterOptions.filter(
      (option) => option.id !== selectedOption.value
    );
    updatePromoterOptions(updatedPromoterOptions);
    setPromoterMenuIsOpen(false);
  };

  const handleCorbanChange = (selectedOption: any) => {
    setSelectedCorban(selectedOption);
    setCorbanId(selectedOption ? selectedOption.value : 0);

    const updatedCorbanOptions = corbanOptions.filter(
      (option) => option.id !== selectedOption.value
    );
    updateCorbanOptions(updatedCorbanOptions);
    setCorbanMenuIsOpen(false);
  };
  // Add handler for responsavel change

  const handleResponsavelChange = (selectedOption: any) => {
    setSelectedResponsavel(selectedOption);
    setCommercialManagerId(selectedOption ? selectedOption.value : "");
    const updatedResponsavelOptions = responsavelOptions.filter(
      (option) => option.id !== selectedOption.value
    );
    updateResponsavelOptions(updatedResponsavelOptions);
    setResponsavelMenuIsOpen(false);
  };
  const handleSellerChange = (selectedOption: any) => {
    setSelectedSeller(selectedOption);
    setSellerId(selectedOption ? selectedOption.value : "");
    const updatedSellerOptions = sellerOptions.filter(
      (option) => option.id !== selectedOption.value
    );

    updateSellerOptions(updatedSellerOptions);
    setSellerMenuIsOpen(false);
  };

  const onCorbanSearchChange = (inputValue: string) => {
    if (inputValue !== "") {
      setCorbanSearch(inputValue);
    }
  };

  const handleCorbanSearchIconClick = async () => {
    await applyCorbanSearch(corbanSearch, 20);
  };

  const handleCorbanKeyDown = (event: any) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyCorbanSearch(corbanSearch, 20);
    }
  };

  const onPromoterSearchChange = (inputValue: string) => {
    if (inputValue !== "") {
      setPromoterSearch(inputValue);
    }
  };

  const handlePromoterSearchIconClick = async () => {
    await applyPromoterSearch(promoterSearch, 20);
  };

  const handlePromoterKeyDown = (event: any) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyPromoterSearch(promoterSearch, 20);
    }
  };

  // Add handlers for responsavel search
  const onResponsavelSearchChange = (inputValue: string) => {
    if (inputValue !== "") {
      setResponsavelSearch(inputValue);
    }
  };

  const handleResponsavelSearchIconClick = async () => {
    await applyResponsavelSearch(responsavelSearch, 20);
  };

  const handleResponsavelKeyDown = (event: any) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyResponsavelSearch(responsavelSearch, 20);
    }
  };

  const onSellerSearchChange = (inputValue: string) => {
    if (inputValue !== "") {
      setSellerSearch(inputValue);
    }
  };

  const handleSellerSearchIconClick = async () => {
    await applySellerSearch(sellerSearch, 20);
  };

  const handleSellerKeyDown = (event: any) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applySellerSearch(sellerSearch, 20);
    }
  };
  const handleMenuScrollToBottomPromoter = () => {
    applyPromoterSearch(responsavelSearch, promoterPage);
    setPromoterPage((prevPage) => prevPage + 10);
  };

  const handleMenuScrollToBottomCorban = () => {
    applyCorbanSearch(responsavelSearch, corbanPage);
    setCorbanPage((prevPage) => prevPage + 10);
  };
  const handleMenuScrollToBottomSeller = () => {
    applySellerSearch(responsavelSearch, sellerPage);
    setSellerPage((prevPage) => prevPage + 10);
  };
  const handleMenuScrollToBottomResponsavel = () => {
    applySellerSearch(responsavelSearch, responsavelPage);
    setResponsavelPage((prevPage) => prevPage + 10);
  };
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: "40px",
      maxWidth: "250px",
      borderColor: controlBorderColor,
      backgroundColor: controlBackgroundColor,
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: menuBackgroundColor,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? optionSelectedBackgroundColor
        : state.isFocused
          ? optionFocusedBackgroundColor
          : "transparent",
      color: state.isSelected ? "white" : optionTextColor,
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: optionTextColor,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: placeholderTextColor,
    }),
    loadingIndicator: (provided: any) => ({
      ...provided,
      color: loadingIndicatorColor,
    }),
  };

  return (
    <Box>
      <HStack spacing={2} mb={4} wrap="wrap">
        {dateRanges.map((range) => (
          <Button
            key={range.days}
            size="sm"
            variant={
              selectedRange === range.days.toString() ? "solid" : "outline"
            }
            colorScheme={
              selectedRange === range.days.toString() ? "green" : "gray"
            }
            onClick={() => handleDateRangeSelect(range.days)}
          >
            {range.label}
          </Button>
        ))}
        <Tooltip label="O intervalo de datas determina como os dados são agrupados" placement="top">
          <IconButton
            aria-label="Informações sobre intervalo de datas"
            icon={<InfoIcon />}
            size="sm"
            variant="ghost"
          />
        </Tooltip>
      </HStack>

      <Flex
        gap={2}
        mb={4}
        wrap="wrap"
        alignItems="center"
        justifyContent="flex-start"
      >
        <Box ref={promoterRef} position="relative" width="250px">
          <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
            Promotora
          </Text>
          <AsyncSelect
            cacheOptions
            defaultOptions={[
              { value: 0, label: "Todos" },
              ...formattedPromoterOptions,
            ]}
            loadOptions={loadPromotersOptions}
            onChange={handlePromoterChange}
            value={
              selectedPromoter ??
              (promoterSearch || { value: 0, label: "Todos" })
            }
            onMenuScrollToBottom={handleMenuScrollToBottomPromoter}
            placeholder="PROMOTORA"
            isDisabled={isLoadingPromoter}
            styles={customStyles}
            noOptionsMessage={() => "Nenhuma promotora encontrada"}
            loadingMessage={() => "Carregando..."}
            menuIsOpen={promoterMenuIsOpen}
            onMenuOpen={() => setPromoterMenuIsOpen(true)}
            onMenuClose={() =>
              promoterPage === 10 &&
              (!promoterSearch || selectedPromoter) &&
              setPromoterMenuIsOpen(false)
            }
            onKeyDown={handlePromoterKeyDown}
            components={{
              DropdownIndicator: () =>
                isLoadingPromoter ? (
                  <Spinner size="sm" mr={2} color="green.500" />
                ) : (
                  <SearchIcon
                    mr={2}
                    ml={1}
                    cursor="pointer"
                    onClick={handlePromoterSearchIconClick}
                    _hover={{ color: "green.500", transform: "scale(1.1)" }}
                    transition="all 0.2s"
                  />
                ),
            }}
            onInputChange={onPromoterSearchChange}
          />
        </Box>
        <Box ref={corbanRef} position="relative" width="250px">
          <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
            Corban
          </Text>
          <AsyncSelect
            cacheOptions
            defaultOptions={[
              { value: 0, label: "Todos" },
              ...formattedCorbanOptions,
            ]}
            loadOptions={loadCorbanOptions}
            onChange={handleCorbanChange}
            value={
              selectedCorban ?? (corbanSearch || { value: 0, label: "Todos" })
            }
            onMenuScrollToBottom={handleMenuScrollToBottomCorban}
            placeholder="Corban"
            isDisabled={isLoading}
            styles={customStyles}
            noOptionsMessage={() => "Nenhum corban encontrado"}
            loadingMessage={() => "Carregando..."}
            menuIsOpen={corbanMenuIsOpen}
            onMenuOpen={() => setCorbanMenuIsOpen(true)}
            onMenuClose={() =>
              corbanPage === 10 &&
              (!corbanSearch || selectedCorban) &&
              setCorbanMenuIsOpen(false)
            } // Close the menu when clicking outside
            onKeyDown={handleCorbanKeyDown}
            components={{
              DropdownIndicator: () =>
                isLoading ? (
                  <Spinner size="sm" mr={2} color="green.500" />
                ) : (
                  <SearchIcon
                    mr={2}
                    ml={1}
                    cursor="pointer"
                    onClick={handleCorbanSearchIconClick}
                    _hover={{ color: "green.500", transform: "scale(1.1)" }}
                    transition="all 0.2s"
                  />
                ),
            }}
            onInputChange={onCorbanSearchChange}
          />
        </Box>
        <Box ref={sellerRef} position="relative" width="250px">
          <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
            Vendedor
          </Text>
          <AsyncSelect
            cacheOptions
            defaultOptions={[
              { value: "", label: "Todos" },

              ...formattedSellerOptions,
            ]}
            loadOptions={loadSellerOptions}
            onChange={handleSellerChange}
            value={
              selectedSeller ?? (sellerSearch || { value: "", label: "Todos" })
            }
            onMenuScrollToBottom={handleMenuScrollToBottomSeller}
            placeholder="Selecione um Vendedor"
            isDisabled={isLoadingSeller}
            styles={customStyles}
            noOptionsMessage={() => "Nenhum vendedor encontrado"}
            loadingMessage={() => "Carregando..."}
            menuIsOpen={sellerMenuIsOpen}
            onMenuOpen={() => setSellerMenuIsOpen(true)}
            onMenuClose={() =>
              sellerPage === 10 &&
              (!sellerSearch || selectedSeller) &&
              setSellerMenuIsOpen(false)
            }
            onKeyDown={handleSellerKeyDown}
            components={{
              DropdownIndicator: () =>
                isLoadingSeller ? (
                  <Spinner size="sm" mr={2} color="green.500" />
                ) : (
                  <SearchIcon
                    mr={2}
                    ml={1}
                    cursor="pointer"
                    onClick={handleSellerSearchIconClick}
                    _hover={{ color: "green.500", transform: "scale(1.1)" }}
                    transition="all 0.2s"
                  />
                ),
            }}
            onInputChange={onSellerSearchChange}
          />
        </Box>
        {/* Replace the Select with AsyncSelect for Responsavel */}
        <Box ref={responsavelRef} position="relative" width="250px">
          <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
            Responsável
          </Text>
          <AsyncSelect
            cacheOptions
            defaultOptions={[
              { value: "", label: "Todos" },
              ...formattedResponsavelOptions,
            ]}
            loadOptions={loadResponsavelOptions}
            onChange={handleResponsavelChange}
            value={
              selectedResponsavel ??
              (responsavelSearch || { value: "", label: "Todos" })
            }
            onMenuScrollToBottom={handleMenuScrollToBottomResponsavel}
            placeholder="Responsavel Comercial"
            isDisabled={isLoading}
            styles={customStyles}
            noOptionsMessage={() => "Nenhum responsável encontrado"}
            loadingMessage={() => "Carregando..."}
            menuIsOpen={responsavelMenuIsOpen}
            onMenuOpen={() => setResponsavelMenuIsOpen(true)}
            onMenuClose={() =>
              responsavelPage === 10 &&
              (!responsavelSearch || selectedResponsavel) &&
              setResponsavelMenuIsOpen(false)
            }
            onKeyDown={handleResponsavelKeyDown}
            components={{
              DropdownIndicator: () =>
                isLoadingResponsavel ? (
                  <Spinner size="sm" mr={2} color="green.500" />
                ) : (
                  <SearchIcon
                    mr={2}
                    ml={1}
                    cursor="pointer"
                    onClick={handleResponsavelSearchIconClick}
                    _hover={{ color: "green.500", transform: "scale(1.1)" }}
                    transition="all 0.2s"
                  />
                ),
            }}
            onInputChange={onResponsavelSearchChange}
          />
        </Box>

        <Flex alignItems="flex-start" gap={4}>
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
              Data Inicial
            </Text>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setSelectedRange("");
              }}
              maxW="155px"
            />
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1} color="gray.600">
              Data Final
            </Text>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setSelectedRange("");
              }}
              maxW="155px"
            />
          </Box>
        </Flex>

        <Button
          colorScheme="green"
          fontWeight="bold"
          px={6}
          py={4}
          mt={6}
          ml={3}
          borderRadius="md"
          onClick={() =>
            applyFilters(
              startDate,
              endDate,
              corbanId,
              promoterId,
              commercialManagerId,
              sellerId
            )
          }
          isDisabled={!startDate || !endDate}
        >
          Filtrar
        </Button>
      </Flex>

      <Flex alignItems="center" mb={2}>
        <Text fontSize="sm" mr={2}>
          Intervalo:
        </Text>
        <Badge colorScheme={rangeIndicator.color} variant="subtle">
          {rangeIndicator.text}
        </Badge>
        {startDate && endDate && (
          <Text fontSize="sm" ml={4} color={textColor}>
            {format(new Date(startDate), "dd/MM/yyyy")} até{" "}
            {format(new Date(endDate), "dd/MM/yyyy")}
          </Text>
        )}
      </Flex>
    </Box>
  );
};

export default FilterComponent;
